/* eslint-disable react-hooks/immutability */
import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Image, Folder, FolderOpen, Undo2 } from 'lucide-react';
import safeStorage from '@/lib/safeStorage';
import logger from '@/lib/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminImageReplacer() {
  const { user } = useAuth();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [targetSrc, setTargetSrc] = useState('');
  const [targetEl, setTargetEl] = useState(null);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [overrides, setOverrides] = useState({});
  const [contextMenu, setContextMenu] = useState(null); // {x, y, src, el}

  const isAdmin = user?.role === 'admin';

  // Load overrides on mount and apply them
  useEffect(() => {
    axios.get(`${API}/image-overrides`).then(r => {
      const map = {};
      r.data.forEach(o => { map[o.original_src] = o.new_src; });
      setOverrides(map);
      // Cache in safeStorage for instant apply on next page load
      safeStorage.set('smb_image_overrides', map);
      // Disconnect the early observer from index.html
      if (window.__imageOverrideObserver) {
        window.__imageOverrideObserver.disconnect();
        window.__imageOverrideObserver = null;
      }
    }).catch(() => {});
  }, []);

  // Apply overrides to all images on the page
  useEffect(() => {
    if (Object.keys(overrides).length === 0) return;
    const imgs = document.querySelectorAll('img');
    imgs.forEach(img => {
      const src = img.getAttribute('src') || '';
      const dataSrc = img.getAttribute('data-original-src');
      const checkSrc = dataSrc || src;
      if (overrides[checkSrc]) {
        if (!dataSrc) img.setAttribute('data-original-src', src);
        img.src = overrides[checkSrc];
      }
    });
  }, [overrides]);

  // Re-apply overrides on route change (MutationObserver)
  useEffect(() => {
    if (Object.keys(overrides).length === 0) return;
    const observer = new MutationObserver(() => {
      const imgs = document.querySelectorAll('img:not([data-override-checked])');
      imgs.forEach(img => {
        img.setAttribute('data-override-checked', '1');
        const src = img.getAttribute('src') || '';
        if (overrides[src]) {
          img.setAttribute('data-original-src', src);
          img.src = overrides[src];
        }
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [overrides]);

  // Right-click handler for admin
  useEffect(() => {
    if (!isAdmin) return;

    const handler = (e) => {
      const img = e.target.closest('img');
      if (!img) return;
      
      e.preventDefault();
      const src = img.getAttribute('data-original-src') || img.getAttribute('src') || '';
      setContextMenu({ x: e.clientX, y: e.clientY, src, el: img });
    };

    document.addEventListener('contextmenu', handler);
    return () => document.removeEventListener('contextmenu', handler);
  }, [isAdmin]);

  // Close context menu on click elsewhere
  useEffect(() => {
    if (!contextMenu) return;
    const handler = () => setContextMenu(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [contextMenu]);

  const openPicker = () => {
    setTargetSrc(contextMenu.src);
    setTargetEl(contextMenu.el);
    setContextMenu(null);
    setPickerOpen(true);
    fetchMedia();
  };

  const restoreOriginal = async () => {
    const src = contextMenu.src;
    setContextMenu(null);
    try {
      await axios.delete(`${API}/image-overrides?original_src=${encodeURIComponent(src)}`, {});
      const next = { ...overrides };
      delete next[src];
      safeStorage.set('smb_image_overrides', next);
      setOverrides(next);
      // Restore the image element
      if (contextMenu.el) contextMenu.el.src = src;
      toast.success('Immagine originale ripristinata');
    } catch { toast.error('Errore'); }
  };

  const fetchMedia = async (folder) => {
    setLoading(true);
    try {
      const [mRes, fRes] = await Promise.all([
        axios.get(`${API}/media`, { params: { folder: folder || undefined } }),
        axios.get(`${API}/media/folders`, {})
      ]);
      setMediaFiles(mRes.data);
      setFolders(fRes.data);
    } catch (err) { logger.error('Fetch error:', err); } finally { setLoading(false); }
  };

  const selectFolder = (f) => {
    setSelectedFolder(f);
    fetchMedia(f);
  };

  const selectImage = async (mediaItem) => {
    const newSrc = mediaItem.url;
    try {
      await axios.post(`${API}/image-overrides?original_src=${encodeURIComponent(targetSrc)}&new_src=${encodeURIComponent(newSrc)}`, {}, {});
      const updated = { ...overrides, [targetSrc]: newSrc };
      safeStorage.set('smb_image_overrides', updated);
      setOverrides(updated);
      if (targetEl) {
        targetEl.setAttribute('data-original-src', targetSrc);
        targetEl.src = newSrc;
      }
      setPickerOpen(false);
      toast.success('Immagine sostituita!');
    } catch { toast.error('Errore nel salvataggio'); }
  };

  const isImage = (name) => {
    const ext = (name || '').toLowerCase().split('.').pop();
    return ['jpg','jpeg','png','gif','webp','svg'].includes(ext);
  };

  const filteredMediaImages = useMemo(() => mediaFiles.filter(m => isImage(m.original_name)), [mediaFiles]);

  if (!isAdmin) return null;

  const hasOverride = contextMenu && overrides[contextMenu.src];

  return (
    <>
      {/* Custom context menu */}
      {contextMenu && (
        <div
          className="fixed z-[200] bg-white rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.2)] border border-[hsl(var(--border))] py-1 min-w-[220px]"
          style={{ left: Math.min(contextMenu.x, window.innerWidth - 240), top: Math.min(contextMenu.y, window.innerHeight - 120) }}
        >
          <button
            onClick={openPicker}
            className="w-full text-left px-4 py-2.5 text-sm hover:bg-[hsl(var(--secondary))] flex items-center gap-2 transition-colors"
          >
            <Image size={15} className="text-[#238A92]" /> Sostituisci immagine
          </button>
          {hasOverride && (
            <button
              onClick={restoreOriginal}
              className="w-full text-left px-4 py-2.5 text-sm hover:bg-[hsl(var(--secondary))] flex items-center gap-2 transition-colors text-amber-600"
            >
              <Undo2 size={15} /> Ripristina originale
            </button>
          )}
          <div className="border-t my-1" />
          <div className="px-4 py-1.5 text-[10px] text-muted-foreground truncate max-w-[220px]">
            {contextMenu.src?.split('/').pop()}
          </div>
        </div>
      )}

      {/* Media Picker Dialog */}
      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle style={{fontFamily:'Space Grotesk'}}>Seleziona nuova immagine</DialogTitle>
          </DialogHeader>
          
          <div className="flex gap-4 flex-1 overflow-hidden">
            {/* Folder sidebar */}
            <div className="w-40 flex-shrink-0 overflow-y-auto">
              <button
                onClick={() => { setSelectedFolder(null); fetchMedia(null); }}
                className={`w-full text-left flex items-center gap-2 px-2.5 py-2 text-sm rounded-lg mb-1 transition-colors ${!selectedFolder ? 'bg-[#238A92] text-white' : 'hover:bg-[hsl(var(--secondary))]'}`}
              >
                <FolderOpen size={14} /> Tutte
              </button>
              {folders.map(f => (
                <button
                  key={f.name}
                  onClick={() => selectFolder(f.name)}
                  className={`w-full text-left flex items-center justify-between px-2.5 py-2 text-sm rounded-lg mb-1 transition-colors ${selectedFolder === f.name ? 'bg-[#238A92] text-white' : 'hover:bg-[hsl(var(--secondary))]'}`}
                >
                  <span className="flex items-center gap-2 truncate"><Folder size={14} /> {f.name}</span>
                  <span className="text-[10px] opacity-60">{f.count}</span>
                </button>
              ))}
            </div>

            {/* Images grid */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="grid grid-cols-4 gap-2">
                  {[1,2,3,4,5,6,7,8].map(i => <Skeleton key={i} className="aspect-square rounded-lg" />)}
                </div>
              ) : filteredMediaImages.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Image size={32} className="mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Nessuna immagine in questa cartella</p>
                </div>
              ) : (
                <div className="grid grid-cols-4 gap-2">
                  {filteredMediaImages.map(m => (
                    <button
                      key={m.id}
                      onClick={() => selectImage(m)}
                      className="aspect-square rounded-lg overflow-hidden border-2 border-transparent hover:border-[#238A92] hover:shadow-md transition-all relative group"
                      title={m.original_name}
                    >
                      <img src={m.url} alt={m.original_name} className="w-full h-full object-cover" loading="lazy" />
                      <div className="absolute inset-x-0 bottom-0 bg-black/70 text-white text-[10px] px-1.5 py-1 truncate opacity-0 group-hover:opacity-100 transition-opacity">
                        {m.original_name}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
